import type { RouteObject } from "react-router";
import {
  appPath,
  clusterPath,
  clusterRunPath,
  computeRedirectByNameAndPitPath,
  datasetPath,
  envPath,
  pitPath,
  taskPath,
  taskRedirectByNameAndPitPath,
  taskRedirectPath,
  taskRunPath,
  transformationPath,
  transformRedirectPath,
} from "./appRoutes.schemas";
import type { RouteDef } from "./appRoutes.types";
import { buildRoutes, pathToMatcher } from "./appRoutes.utils";

// ---------------------------------------------------------------------------
// Static (no dynamic params) routes
// ---------------------------------------------------------------------------
// Page `import()` specifiers use `as string` so TypeScript treats them as
// dynamic imports (no static module resolution). The cast erases in emit, so
// the bundler still receives a literal path for code-splitting. It makes cidrular
// dependencies and type checking pass.

const staticRoutes = buildRoutes({
  login: {
    key: "login",
    path: "/login",
    name: "Login",
    hasBreadcrumbs: false,
    lazy: () => import("src/pages/AuthPages/LoginPage/index.tsx" as string),
  },
  logout: {
    key: "logout",
    path: "/logout",
    name: "Logout",
    hasBreadcrumbs: false,
    // No lazy — LogoutUtilityPage is rendered as a static element in routerBuilder
  },
  signup: {
    key: "signup",
    path: "/signup",
    name: "Signup",
    hasBreadcrumbs: false,
    lazy: () => import("src/pages/AuthPages/SignupPage/index.tsx" as string),
  },
  "signup-thank-you": {
    key: "signup-thank-you",
    path: "/signup-thank-you",
    name: "SignupThankYou",
    hasBreadcrumbs: false,
    lazy: () => import("src/pages/AuthPages/SignupThankYouPage/index.tsx" as string),
  },
  "signup-set-password": {
    key: "signup-set-password",
    path: "/signup-set-password",
    name: "SignupSetPassword",
    hasBreadcrumbs: false,
    lazy: () =>
      import("src/pages/AuthPages/ResetPassword/SetPassword.tsx" as string),
  },
  "forgot-password": {
    key: "forgot-password",
    path: "/forgot-password",
    name: "ForgotPassword",
    hasBreadcrumbs: false,
    lazy: () =>
      import("src/pages/AuthPages/ResetPassword/ForgotPassword.tsx" as string),
  },
  "change-password": {
    key: "change-password",
    path: "/change-password",
    name: "ChangePassword",
    hasBreadcrumbs: false,
    lazy: () =>
      import("src/pages/AuthPages/ResetPassword/ChangePassword.tsx" as string),
  },
  "compare-runs": {
    key: "compare-runs",
    path: "/compare-runs",
    name: "Compare-runs",
    hasBreadcrumbs: false,
    lazy: () =>
      import(
        "src/pages/ComparativeAnalysisPage/ComparativeAnalysisPage.tsx" as string
      ),
  },
  users: {
    key: "users",
    path: "/users",
    name: "Users",
    hasBreadcrumbs: false,
    lazy: () => import("src/pages/UsersPage/UsersPage.tsx" as string),
  },
  license: {
    key: "license",
    path: "/license",
    name: "License",
    hasBreadcrumbs: false,
    lazy: () => import("src/pages/LicensePage/LicensePage.tsx" as string),
  },
  welcome: {
    key: "welcome",
    path: "/welcome",
    name: "Welcome",
    hasBreadcrumbs: false,
    lazy: () => import("src/pages/WelcomePage/WelcomePage.tsx" as string),
  },
  "super-admin": {
    key: "super-admin",
    path: "/super-admin",
    name: "SuperAdmin",
    hasBreadcrumbs: false,
    lazy: () => import("src/super-admin/SuperAdmin.tsx" as string),
  },
  token: {
    key: "token",
    path: "/token/:token",
    name: "Token",
    hasBreadcrumbs: false,
    lazy: () => import("src/pages/AuthPages/TokenAuth/TokenAuth.tsx" as string),
    id: "token",
  },
  "set-password": {
    key: "set-password",
    path: "/set-password/:token",
    name: "SetPassword",
    hasBreadcrumbs: false,
    lazy: () =>
      import("src/pages/AuthPages/ResetPassword/SetPassword.tsx" as string),
  },
} satisfies { [K in string]: RouteDef<K> });

// ---------------------------------------------------------------------------
// Env-scoped routes (require env_name at minimum)
// ---------------------------------------------------------------------------

export const envRoutes = buildRoutes({
  // ---- section index routes (used by sidebar / breadcrumbs / useBuildLink) ----
  apps: {
    key: "apps",
    path: "envs/:env_name/apps",
    name: "Apps",
    hasBreadcrumbs: true,
    schema: envPath,
    lazy: () => import("src/pages/PipelinesIndexPage/PipelinesPage.tsx" as string),
  },
  compute: {
    key: "compute",
    path: "envs/:env_name/compute",
    name: "Compute",
    hasBreadcrumbs: true,
    schema: envPath,
    lazy: () => import("src/pages/computes/ComputePage/ComputePage.tsx" as string),
  },
  runs: {
    key: "runs",
    path: "envs/:env_name/runs",
    name: "Runs",
    hasBreadcrumbs: true,
    schema: envPath,
    lazy: () => import("src/pages/RunsIndexPage/RunsIndexPage.tsx" as string),
  },
  datasets: {
    key: "datasets",
    path: "envs/:env_name/datasets",
    name: "Datasets",
    hasBreadcrumbs: true,
    schema: envPath,
    lazy: () => import("src/pages/Datasets/DatasetsPage/DatasetsPage.tsx" as string),
  },
  alerts: {
    key: "alerts",
    path: "envs/:env_name/alerts",
    name: "Alerts",
    hasBreadcrumbs: true,
    schema: envPath,
    lazy: () => import("src/pages/AlertsIndexPage/AlertsIndexPage.tsx" as string),
  },

  // ---- deep pipeline routes ----
  app: {
    key: "app",
    path: "envs/:env_name/apps/:app_name",
    name: "App",
    hasBreadcrumbs: true,
    schema: appPath,
    lazy: () => import("src/pages/PipelinePage/PipelinePage.tsx" as string),
  },
  "pipeline-run": {
    key: "pipeline-run",
    path: "envs/:env_name/apps/:app_name/:app_pit",
    name: "PipelineRun",
    hasBreadcrumbs: true,
    schema: pitPath,
    lazy: () => import("src/pages/PipelineRunPage/PipelineRunPage.tsx" as string),
  },
  "task-page": {
    key: "task-page",
    path: "envs/:env_name/apps/:app_name/0/:task_name",
    name: "TaskPage",
    hasBreadcrumbs: true,
    schema: taskPath,
    lazy: () => import("src/pages/TaskPage/TaskPage.tsx" as string),
  },
  "task-run": {
    key: "task-run",
    path: "envs/:env_name/apps/:app_name/:app_pit/:task_id",
    name: "TaskRun",
    hasBreadcrumbs: true,
    schema: taskRunPath,
    lazy: () => import("src/pages/TaskRunPage/TaskRunPage.tsx" as string),
  },
  transformation: {
    key: "transformation",
    path: "envs/:env_name/apps/:app_name/:app_pit/:task_id/:tf_id",
    name: "Transformation",
    hasBreadcrumbs: true,
    schema: transformationPath,
    lazy: () =>
      import("src/pages/TransformationPage/TransformationPage.tsx" as string),
  },

  // ---- deep compute routes ----
  cluster: {
    key: "cluster",
    path: "envs/:env_name/compute/:app_name/:cluster_name",
    name: "Cluster",
    hasBreadcrumbs: true,
    schema: clusterPath,
    lazy: () => import("src/pages/computes/ClusterPage/ClusterPage.tsx" as string),
  },
  "cluster-run": {
    key: "cluster-run",
    path: "envs/:env_name/compute/:app_name/:cluster_name/:run_time/:task_id",
    name: "ClusterRun",
    hasBreadcrumbs: true,
    schema: clusterRunPath,
    lazy: () =>
      import("src/pages/computes/ClusterRunPage/ClusterRunPage.tsx" as string),
  },

  // ---- datasets deep route ----
  dataset: {
    key: "dataset",
    path: "envs/:env_name/datasets/:ds_name",
    name: "Dataset",
    hasBreadcrumbs: true,
    schema: datasetPath,
    buildLinkParamMapper: ({ dataset }: { dataset: string }) => ({
      ds_name: dataset,
    }),
    lazy: () => import("src/pages/Datasets/DatasetPage/DatasetPage.tsx" as string),
  },

  // ---- dashboard ----
  dashboard: {
    key: "dashboard",
    path: "envs/:env_name",
    name: "Dashboard",
    hasBreadcrumbs: false,
    schema: envPath,
    getBasePath: (env_name: string) => `/envs/${encodeURIComponent(env_name)}`,
    lazy: () => import("src/pages/EnvDashboardPage/EnvDashboardPage.tsx" as string),
  },
} satisfies { [K in string]: RouteDef<K, any, any> });

// ---------------------------------------------------------------------------
// Redirect-only routes — shortcut URLs resolved entirely by router loaders.
// No page component; each entry collocates path pattern + Zod schema.
// `route` contains only `path` — the caller must add the loader (circular
// dependency prevents importing loaders here).
// ---------------------------------------------------------------------------

export const redirectRoutes = {
  "task-by-id": {
    path: "tasks/:task_id",
    schema: taskRedirectPath,
    route: { path: "tasks/:task_id" } satisfies RouteObject,
  },
  "task-by-name-and-pit": {
    path: "tasks/:env_name/:app_name/:task_name/:app_pit",
    schema: taskRedirectByNameAndPitPath,
    route: {
      path: "tasks/:env_name/:app_name/:task_name/:app_pit",
    } satisfies RouteObject,
  },
  "compute-by-name-and-pit": {
    path: "compute/:env_name/:app_name/:cluster_name/:app_pit",
    schema: computeRedirectByNameAndPitPath,
    route: {
      path: "compute/:env_name/:app_name/:cluster_name/:app_pit",
    } satisfies RouteObject,
  },
  "transform-by-id": {
    path: "transforms/:tf_id",
    schema: transformRedirectPath,
    route: { path: "transforms/:tf_id" } satisfies RouteObject,
  },
} as const;

// ---------------------------------------------------------------------------
// Unified export
// ---------------------------------------------------------------------------

export const appRoutes = {
  ...staticRoutes,
  ...envRoutes,
} as const;

export type AppRouteKey = keyof typeof appRoutes;
export type StaticRouteKey = keyof typeof staticRoutes;
export type EnvRouteKey = keyof typeof envRoutes;

// ---------------------------------------------------------------------------
// Iteration helpers
// ---------------------------------------------------------------------------

/**
 * All route entries sorted by descending path segment count so more-specific
 * matchers are tried before shorter ones (e.g. transformation before task-run).
 */
const routeList = [...Object.values(staticRoutes), ...Object.values(envRoutes)].sort(
  (a, b) => b.path.split("/").length - a.path.split("/").length
);

export const getRouteObject = (pathname: string) =>
  routeList.find((r) => r.matcher(pathname));

const redirectRouteMatchers = Object.values(redirectRoutes).map((r) =>
  pathToMatcher(r.path)
);

export const isKnownAppRoutePathname = (pathname: string) =>
  !!getRouteObject(pathname) ||
  redirectRouteMatchers.some((matcher) => matcher(pathname));

/**
 * Env-dependent routes for the router and modal memory router.
 * In these routers the paths are relative to `envs/:env_name` so the prefix is stripped.
 */
export const envPageRoutes: RouteObject[] = Object.values(envRoutes)
  .filter((r) => r.path !== "envs/:env_name")
  .map((r) => ({ ...r.route, path: r.route.path?.replace("envs/:env_name/", "") }));
